import { useState } from "react";
import { useNavigate } from "react-router-dom";
import notification from "../../../../helpers/notification";
import TspApiService from "../../api/tspApiService";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import { WORKFLOW_ACTION_UI } from "../../constants/tspJourneyStatus";
import { buildQuickActions } from "./journeyDetailModel";
import JourneyCancelModal from "./components/JourneyCancelModal";
import { TspConfirmDialog } from "../../components/primitives";

// The Quick Actions rail (Edit, Record City Visit, and the workflow buttons
// `allowed_actions` permits) plus the dialogs those buttons open (Edit opens
// the Edit Journey page, the same Journey Builder as Add Journey) — shared by
// Update Journey and Your Stats so both screens act on a journey the exact
// same way and never drift into two implementations of the same rules.
//
// `journey` is the full journey detail (with `capabilities`/`allowed_actions`),
// or undefined/null while it is still loading — a hook, so it must be called
// on every render regardless, and is a no-op until `journey` arrives.
// `reload` re-fetches the caller's own data after a successful change.
export default function useTspJourneyWorkflowActions(journey, reload) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null); // null | "cancel" | a workflow action name
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  if (!journey) return { quickActions: [], handleAction: () => {}, dialogs: null };

  const closeDialog = () => {
    setDialog(null);
    setActionError("");
  };

  const runAction = async (action, body) => {
    setBusy(true);
    setActionError("");
    try {
      await TspApiService.transitionJourney(journey.id, action, body);
      notification("success", WORKFLOW_ACTION_UI[action].done);
      closeDialog();
      reload();
    } catch (error) {
      const message = tspErrorMessage(error, "The journey could not be updated.");
      notification("error", message);
      setActionError(message);
      if (error?.response?.status === 409) reload(); // refused: show the journey's real state
    } finally {
      setBusy(false);
    }
  };

  const handleAction = (key) => {
    if (key === "record_visit") navigate(`/tsp/city-visits?journey=${journey.id}`);
    else if (key === "edit") navigate(`/tsp/journeys/${journey.id}/edit`);
    else setDialog(key);
  };

  const quickActions = buildQuickActions(journey).map((action) => ({ ...action, disabled: busy }));
  const workflowUi = WORKFLOW_ACTION_UI[dialog];

  const dialogs = (
    <>
      {dialog === "cancel" && <JourneyCancelModal busy={busy} error={actionError} onClose={closeDialog} onConfirm={(reason) => runAction("cancel", { reason })} />}
      {workflowUi && dialog !== "cancel" && (
        <TspConfirmDialog
          open
          title={workflowUi.label}
          message={actionError || workflowUi.confirm}
          confirmLabel={workflowUi.label}
          busy={busy}
          onConfirm={() => runAction(dialog)}
          onCancel={closeDialog}
        />
      )}
    </>
  );

  return { quickActions, handleAction, dialogs };
}
